import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { setAppVersionConfigCallable, wasReportedByCallable } from '@/lib/callables';
import {
  gateIsActive,
  subscribeAppVersions,
  type AppVersionRow,
  type AppVersionsResult,
} from '@/lib/firestoreAppVersions';
import { TermsVersionContainer } from './TermsVersionContainer';

const REASON_MIN = 3;

type Confirming = {
  row: AppVersionRow;
  minBuild: string;
  reason: string;
};

/** Data/hora local de um epoch em ms, ou `—` quando ausente. */
function formatWhen(epochMs: number | null | undefined, locale: string): string {
  if (epochMs === null || epochMs === undefined) return '—';
  return new Date(epochMs).toLocaleString(locale);
}

/**
 * Kill switch de versão (#655) — `/settings/app-versions`.
 *
 * Antes desta tela, apertar o `minBuild` era editar `/appConfig/{platform}` à
 * mão no console do Firebase: sem guard, sem audit e contra a regra que proíbe
 * escrita direta no Firestore.
 *
 * A tela mostra dois números com donos diferentes, e é essa distinção que ela
 * existe para tornar óbvia:
 *
 * - **`latestBuild`** é fato. Quem escreve é o release (web/admin/android) ou o
 *   webhook da App Store Connect (iOS). Não se edita aqui.
 * - **`minBuild`** é decisão. Apertá-lo trava todos os aparelhos abaixo daquele
 *   build, numa tela que não tem saída por desenho.
 *
 * Origem: #655 — commit acba8e71e, 2026-09-16 17:04 +0200 (tela do kill
 * switch de versão).
 *
 * Contrato: assina `subscribeAppVersions` (os quatro docs de `/appConfig`).
 * Apertar o `minBuild` exige número inteiro ≥ 0, não acima do `latestBuild`
 * publicado, e motivo com pelo menos `REASON_MIN` caracteres; grava pela
 * callable `setAppVersionConfig` (que revalida tudo e audita). Falha → `Alert`
 * dentro do modal (que fica aberto) e `reportNonFatal`. Abaixo da tabela, o card da versão dos Termos
 * (`TermsVersionContainer`).
 *
 * Usado por: rota `settings/app-versions` (`routes/router.tsx`), menu Sistema
 * (`AdminLayout`). Spec: `docs/screens/app-version-gate.md`.
 *
 * Dados: `appConfig/{ios|android|web|admin}` — `AppVersionConfigSchema` de
 * `@etiquetachef/types`; rule `match /appConfig/{platform}` (leitura aberta,
 * escrita só pela callable). Detalhe em `lib/firestoreAppVersions.ts`.
 *
 * Dicas: os clientes (`useAppVersionGate` aqui, o gate do app web, Android e
 * iOS) caem em OK quando o doc falta ou não valida — apertar o `minBuild`
 * errado trava aparelhos de verdade, por isso o motivo obrigatório.
 *
 * Issues: #655, #1021 (erro dentro do modal; mesmo achado do CRM, #174).
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) recusa do servidor em `Alert` no modal (o
 *    toast ficava atrás do overlay) e report no PostHog (era só `console.error`).
 */
export function AppVersionGatePage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<AppVersionsResult>({ kind: 'LOADING' });
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [saving, setSaving] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): recusa do servidor, mostrada dentro do modal.
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => subscribeAppVersions(setState), []);

  const parsedMinBuild = confirming === null ? Number.NaN : Number.parseInt(confirming.minBuild, 10);
  const latestBuild = confirming?.row.config?.latestBuild ?? 0;
  const currentMinBuild = confirming?.row.config?.minBuild ?? 0;

  // As três razões para o botão ficar desabilitado são distintas, e cada uma
  // tem sua mensagem — "inválido" sozinho faria a pessoa adivinhar qual é.
  const minBuildInvalid = !Number.isInteger(parsedMinBuild) || parsedMinBuild < 0;
  const minBuildAboveLatest = !minBuildInvalid && parsedMinBuild > latestBuild;
  const reasonTooShort = (confirming?.reason.trim().length ?? 0) < REASON_MIN;
  const canSubmit = !minBuildInvalid && !minBuildAboveLatest && !reasonTooShort && !saving;

  async function submit(): Promise<void> {
    if (confirming === null || !canSubmit) return;
    setSaving(true);
    setSaveError(null);
    try {
      await setAppVersionConfigCallable({
        platform: confirming.row.platform,
        minBuild: parsedMinBuild,
        reason: confirming.reason.trim(),
      });
      toast.success(t('admin.appVersionGate.saved'));
      setConfirming(null);
    } catch (err) {
      // O servidor é quem decide; o cliente só espelha. Um `minBuild` acima do
      // publicado é recusado lá mesmo que a UI deixasse passar.
      console.error('admin/appVersionGate: falha ao apertar', err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay deste Modal, que
      // não fecha no erro (mesmo achado do CRM, #174); e o `console.error` sozinho não chega a nós.
      // 2ª auditoria (2026-10-25 15:52 +0100, #1021): falha da callable já sai como `callable_failed`
      // pelo wrapper; aqui só o que ele não vê (Zod do input, offline) — senão eram 2 eventos.
      if (!wasReportedByCallable(err)) {
        reportNonFatal('admin_app_version_gate_save_failed', serializeError(err));
      }
      setSaveError(t('admin.appVersionGate.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.appVersionGate.subtitle')}>
          {t('admin.appVersionGate.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Alert type="info" header={t('admin.appVersionGate.explainTitle')}>
          {t('admin.appVersionGate.explainBody')}
        </Alert>

        <Container>
          {state.kind === 'LOADING' && (
            <Box textAlign="center" padding="l">
              <Spinner size="large" />
            </Box>
          )}
          {state.kind === 'ERROR' && (
            <Alert type="error">{t('admin.appVersionGate.loadFailed')}</Alert>
          )}
          {state.kind === 'READY' && (
            <Table<AppVersionRow>
              variant="embedded"
              items={state.rows}
              columnDefinitions={[
                {
                  id: 'platform',
                  header: t('admin.appVersionGate.colPlatform'),
                  cell: (row) => t(`admin.appVersionGate.platform.${row.platform}`),
                },
                {
                  id: 'latest',
                  header: t('admin.appVersionGate.colLatest'),
                  cell: (row) => row.config?.latestVersion ?? '—',
                },
                {
                  id: 'minBuild',
                  header: t('admin.appVersionGate.colMinBuild'),
                  cell: (row) => (row.config === null ? '—' : String(row.config.minBuild)),
                },
                {
                  id: 'state',
                  header: t('admin.appVersionGate.colState'),
                  cell: (row) =>
                    row.config === null ? (
                      <Badge color="grey">{t('admin.appVersionGate.neverPublished')}</Badge>
                    ) : gateIsActive(row.config) ? (
                      <Badge color="red">{t('admin.appVersionGate.stateActive')}</Badge>
                    ) : (
                      <Badge color="green">{t('admin.appVersionGate.stateIdle')}</Badge>
                    ),
                },
                {
                  id: 'updatedAt',
                  header: t('admin.appVersionGate.colUpdatedAt'),
                  cell: (row) => formatWhen(row.config?.updatedAt, i18n.language),
                },
                {
                  id: 'actions',
                  header: '',
                  cell: (row) => (
                    <Button
                      variant="normal"
                      disabled={row.config === null}
                      onClick={() => {
                        setSaveError(null);
                        setConfirming({
                          row,
                          minBuild: String(row.config?.minBuild ?? 0),
                          reason: '',
                        });
                      }}
                    >
                      {t('admin.appVersionGate.change')}
                    </Button>
                  ),
                },
              ]}
              empty={<Box textAlign="center">{t('admin.appVersionGate.empty')}</Box>}
            />
          )}
        </Container>

        <TermsVersionContainer />
      </SpaceBetween>

      <Modal
        visible={confirming !== null}
        onDismiss={() => {
          if (!saving) setConfirming(null);
        }}
        header={t('admin.appVersionGate.modalTitle')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                disabled={saving}
                onClick={() => {
                  setConfirming(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={!canSubmit}
                loading={saving}
                onClick={() => {
                  void submit();
                }}
              >
                {t('admin.appVersionGate.confirm')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {confirming !== null && (
          <SpaceBetween size="m">
            {/* O efeito, em uma frase, antes do formulário. Sem isto, apertar o
                gate parece editar um número. */}
            <Alert type="warning">
              {t('admin.appVersionGate.modalWarning', {
                platform: t(`admin.appVersionGate.platform.${confirming.row.platform}`),
              })}
            </Alert>

            <FormField
              label={t('admin.appVersionGate.minBuildLabel')}
              description={t('admin.appVersionGate.minBuildHint', {
                latest: latestBuild,
                current: currentMinBuild,
              })}
              errorText={
                minBuildInvalid
                  ? t('admin.appVersionGate.errInvalid')
                  : minBuildAboveLatest
                    ? t('admin.appVersionGate.errAboveLatest', { latest: latestBuild })
                    : undefined
              }
            >
              <Input
                type="number"
                value={confirming.minBuild}
                onChange={({ detail }) => {
                  setConfirming({ ...confirming, minBuild: detail.value });
                }}
              />
            </FormField>

            {!minBuildInvalid && !minBuildAboveLatest && (
              <Box variant="p">
                {parsedMinBuild === 0
                  ? t('admin.appVersionGate.effectNone')
                  : t('admin.appVersionGate.effectBlocks', { min: parsedMinBuild })}
              </Box>
            )}

            <FormField
              label={t('admin.appVersionGate.reasonLabel')}
              description={t('admin.appVersionGate.reasonHint')}
              errorText={
                reasonTooShort && confirming.reason.length > 0
                  ? t('admin.appVersionGate.errReason')
                  : undefined
              }
            >
              <Textarea
                value={confirming.reason}
                onChange={({ detail }) => {
                  setConfirming({ ...confirming, reason: detail.value });
                }}
              />
            </FormField>
            {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do modal, junto do footer (#174). */}
            {saveError !== null && <Alert type="error">{saveError}</Alert>}
          </SpaceBetween>
        )}
      </Modal>
    </ContentLayout>
  );
}
